import { z } from 'zod';

import {
  SELF_SERVICE_CATEGORY_KEYS,
  type CustomerSignUpRequest,
  type SelfServiceCategoryKey,
} from '@mds/shared/types/customer';
import { email, optional, phone, picked, required, tooLong } from '@mds/shared/lib/validation';

/** The channels the CRM records consent for, in the order the form lists them. */
export const CHANNELS = ['email', 'whatsapp', 'sms'] as const;

/** The API's cap on one interest label. */
const LABEL_MAX = 64;

/** Mirrors `customerSignUpSchema` in the API's customer-sign-up module. */
export const signUpSchema = z
  .object({
    name: required('Full name').max(160, tooLong(160)),
    email,
    phone: optional(phone),
    categoryKey: picked('Choose the one that fits best.').refine(
      (key): key is SelfServiceCategoryKey =>
        (SELF_SERVICE_CATEGORY_KEYS as readonly string[]).includes(key),
      'Choose the one that fits best.',
    ),
    interests: z.array(z.string()),
    /** One more interest in the visitor's own words, added to the ticked ones. */
    otherInterest: z.string().trim().max(LABEL_MAX, tooLong(LABEL_MAX)),
    channels: z.array(z.enum(CHANNELS)),
  })
  .transform((values): CustomerSignUpRequest => ({
    name: values.name,
    email: values.email,
    ...(values.phone ? { phone: values.phone } : {}),
    categoryKey: values.categoryKey as SelfServiceCategoryKey,
    // Typed in as well as ticked counts once: the API refuses a label twice.
    interests: [...values.interests, values.otherInterest].filter(
      (label, i, all) =>
        label !== '' && all.findIndex((other) => other.toLowerCase() === label.toLowerCase()) === i,
    ),
    communicationPreferences: {
      email: values.channels.includes('email'),
      whatsapp: values.channels.includes('whatsapp'),
      sms: values.channels.includes('sms'),
    },
  }));

export type SignUpForm = z.input<typeof signUpSchema>;

export const signUpDefaults: SignUpForm = {
  name: '',
  email: '',
  phone: '',
  categoryKey: '',
  interests: [],
  otherInterest: '',
  // Unticked: consent is something the visitor gives, not something they find already given.
  channels: [],
};
